import React from 'react';
import { useEarningsData } from 'hooks/useEarningsQuery';

interface FilterOptions {
  isWatchListOnly?: boolean;
  isEarningsOnly?: boolean;
  watchListTickers?: string[];
}

interface EarningsMap {
  earnings?: Record<string, any>;
}

export type HasTicker = { ticker: string } & { [K: string]: any };

interface UseWatchlistEarningsFilterReturn<T extends HasTicker> {
  filterTickers: (items: T[], options: FilterOptions) => T[];
}

export const useWatchlistEarningsFilter = <T extends HasTicker>(
  earningsMap?: EarningsMap,
): UseWatchlistEarningsFilterReturn<T> => {
  const { hasEarnings } = useEarningsData(earningsMap?.earnings);

  const filterTickers = React.useCallback(
    (
      items: T[],
      { isWatchListOnly = false, isEarningsOnly = false, watchListTickers = [] }: FilterOptions,
    ): T[] => {
      if (!Array.isArray(items)) return [];

      if (isWatchListOnly && items.length > 1 && watchListTickers.length > 1) {
        const watchlist = new Set(watchListTickers);
        return items.filter((item) => {
          if (!watchlist.has(item.ticker)) return false;
          if (isEarningsOnly && !hasEarnings(item.ticker)) return false;
          return true;
        });
      }

      return items.filter((item: T): boolean => {
        if (isWatchListOnly && !watchListTickers.includes(item.ticker)) {
          return false;
        }

        if (isEarningsOnly && !hasEarnings(item.ticker)) {
          return false;
        }

        return true;
      });
    },
    [hasEarnings],
  );

  return { filterTickers };
};
